import { ReactNode } from 'react';

// One sheet of the drawing set. The sheet number sits in the frame corner,
// like on a printed drawing, never as a label above the heading.
interface SheetProps {
	id?: string;
	code: string;
	title?: string;
	children: ReactNode;
	className?: string;
}

export function Sheet({ id, code, title, children, className = '' }: SheetProps) {
	return (
		<section id={id} aria-labelledby={id ? `${id}-heading` : undefined} className={`relative border-t border-ink-line ${className}`}>
			<div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 mx-auto flex max-w-[1320px] justify-end px-4 sm:px-8'>
				<span className='lettering -mt-px border border-t-0 border-ink-line bg-sheet px-2 py-1 text-label text-ink-faint'>
					{title ? `${title} · ` : ''}
					{code}
				</span>
			</div>
			{children}
		</section>
	);
}

// Heading for a sheet; the id links it to the section for screen readers.
export function SheetHeading({ id, children, className = '' }: { id: string; children: ReactNode; className?: string }) {
	return (
		<h2 id={`${id}-heading`} className={`type-wide text-[clamp(2rem,4.2vw,3.5rem)] font-bold leading-[1.02] tracking-tight text-ink ${className}`}>
			{children}
		</h2>
	);
}

// The numbered drafting keynote: a circle with its number, used to tie lists to Fig. 1.
export function Keynote({ n, active = false, small = false, className = '' }: { n: number; active?: boolean; small?: boolean; className?: string }) {
	return (
		<span
			aria-hidden='true'
			className={`inline-flex shrink-0 items-center justify-center rounded-full border font-bold ${small ? 'h-6 w-6 text-xs' : 'h-8 w-8 text-sm'} transition-colors duration-300 ${
				active ? 'border-redline bg-redline text-sheet-deep' : 'border-ink-soft text-ink'
			} ${className}`}>
			{n}
		</span>
	);
}
